import {
  Component, ChangeDetectionStrategy,
  OnInit, ViewChild, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { ParametrelerComponent } from '../../../../service/fikoyt-ui/src/lib/parametreler/parametreler.component';
import { KartWebSocketService } from '@uygulamalar/hmi-pano/kart-okuyucu/services/kart-websocket.service';
import {
  HMI_AYAR_SEKMELERI, IHmiAyarSekmesi,
  hmiAyarlariYukle, hmiAyarlariKaydet, hmiAyarGetir,
} from '../config/hmi-parametre-setleri';

@Component({
  selector: 'hmi-pano-ayarlar',
  standalone: true,
  imports: [
    CommonModule, RouterModule,
    NzTabsModule, NzIconModule, NzButtonModule,
    NzPopconfirmModule, NzTagModule, NzDividerModule, NzAlertModule,
    ParametrelerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ayarlar.component.html',
  styleUrl: './ayarlar.component.less',
})
export class HmiPanoAyarlarComponent extends FikoYTKokBilesenComponent implements OnInit {
  private wsService = inject(KartWebSocketService);

  constructor() { super(); }

  sekmeler: IHmiAyarSekmesi[] = [];
  aktifSekme = 0;

  get toplamParametreSayisi(): number {
    return this.sekmeler.reduce((t, s) =>
      t + s.parametreler.filter(p => !p.ismi.startsWith('_')).length, 0);
  }

  ngOnInit() {
    // Hazır setleri klonla (referans karışmaması için)
    this.sekmeler = HMI_AYAR_SEKMELERI.map(s => ({
      ...s,
      parametreler: s.parametreler.map(p => ({ ...p, ayarlar: { ...p.ayarlar } })),
    }));

    // localStorage'dan mevcut değerleri yükle
    hmiAyarlariYukle(this.sekmeler);

    // Cihaz kimliği yoksa otomatik oluştur
    const cihazP = this.parametreBul('cihazKimlik');
    if (cihazP && !cihazP.degeri) {
      cihazP.degeri = localStorage.getItem('fikoyt_cihaz_kimlik') || this.cihazKimlikOlustur();
    }

    // Mevcut localStorage değerlerini de aktar (geriye uyumluluk)
    this.eskiAyarlariAktar();

    this.CD.markForCheck();
  }

  /** Parametre değişti olayı */
  parametreDegisti(olay: any, sekme: IHmiAyarSekmesi) {
    // Bazı parametreler anında uygulanır
    if (olay?.parametre?.ismi === 'karanlikMod') {
      const deger = olay.parametre.degeri;
      document.body.classList.toggle('hmi-karanlik', !!deger);
      document.documentElement.classList.toggle('tema-karanlik', !!deger);
      document.body.classList.toggle('tema-karanlik', !!deger);
      localStorage.setItem('hmi-karanlik-mod', deger ? '1' : '0');
    }
    if (olay?.parametre?.ismi === 'tamEkran') {
      if (olay.parametre.degeri) {
        document.documentElement.requestFullscreen?.().catch(() => {});
      } else {
        document.exitFullscreen?.().catch(() => {});
      }
    }
  }

  /** Tüm ayarları localStorage'a kaydet */
  kaydet() {
    hmiAyarlariKaydet(this.sekmeler);

    // Socket.IO üzerinden admin panele bildir
    this.wsService.olayGonder({
      olay_kodu: 'ayarlar_guncellendi',
      aciklama: 'HMI ayarları güncellendi',
      veri: this.tumAyarlariTopla(),
    });

    this.nzMessage.success('Tüm ayarlar kaydedildi');
  }

  /** Varsayılana sıfırla */
  sifirla() {
    localStorage.removeItem('hmi-pano-ayarlar');
    this.sekmeler = HMI_AYAR_SEKMELERI.map(s => ({
      ...s,
      parametreler: s.parametreler.map(p => ({ ...p, ayarlar: { ...p.ayarlar } })),
    }));
    this.CD.markForCheck();
    this.nzMessage.info('Ayarlar varsayılana döndürüldü');
  }

  /** Ayarları JSON olarak dışa aktar */
  disaAktar() {
    const veri = this.tumAyarlariTopla();
    const blob = new Blob([JSON.stringify(veri, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hmi-ayarlar-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.nzMessage.success('Ayarlar dışa aktarıldı');
  }

  /** JSON dosyasından içe aktar */
  iceAktar() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e: any) => {
      const dosya = e.target.files?.[0];
      if (!dosya) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const veri = JSON.parse(reader.result as string);
          for (const sekme of this.sekmeler) {
            for (const p of sekme.parametreler) {
              if (p.ismi.startsWith('_')) continue;
              if (veri[p.ismi] !== undefined) {
                p.degeri = veri[p.ismi];
              }
            }
          }
          this.CD.markForCheck();
          this.nzMessage.success('Ayarlar içe aktarıldı — Kaydet butonuna basarak uygulayın');
        } catch {
          this.nzMessage.error('Geçersiz JSON dosyası');
        }
      };
      reader.readAsText(dosya);
    };
    input.click();
  }

  // ─── YARDIMCI ────────────────────────────────────────────────────────────

  private parametreBul(ismi: string): any {
    for (const s of this.sekmeler) {
      const p = s.parametreler.find(p => p.ismi === ismi);
      if (p) return p;
    }
    return null;
  }

  private tumAyarlariTopla(): Record<string, any> {
    const veri: Record<string, any> = {};
    for (const sekme of this.sekmeler) {
      for (const p of sekme.parametreler) {
        if (p.ismi.startsWith('_')) continue;
        veri[p.ismi] = p.degeri;
      }
    }
    return veri;
  }

  private cihazKimlikOlustur(): string {
    const hex = () => Math.floor(Math.random() * 0xFFFFFFFF).toString(16).toUpperCase().padStart(8, '0');
    return `HMI-${hex()}`;
  }

  /** Eski localStorage key'lerinden parametre değerlerini aktar */
  private eskiAyarlariAktar() {
    const mapping: Record<string, string> = {
      'fikoyt_cihaz_ismi': 'cihazIsmi',
      'hmi-karanlik-mod': 'karanlikMod',
    };
    for (const [storageKey, paramIsmi] of Object.entries(mapping)) {
      const p = this.parametreBul(paramIsmi);
      if (p && (p.degeri === null || p.degeri === undefined)) {
        const val = localStorage.getItem(storageKey);
        if (val !== null) {
          if (paramIsmi === 'karanlikMod') {
            p.degeri = val === '1';
          } else {
            p.degeri = val;
          }
        }
      }
    }

    // Kart okuyucu ayarlarını da aktar
    try {
      const kartAyar = JSON.parse(localStorage.getItem('hmi-kart-okuyucu-ayarlar') || '{}');
      if (kartAyar.mod) {
        const modP = this.parametreBul('kartBaglantiModu');
        if (modP && !modP.degeri) modP.degeri = kartAyar.mod;
      }
      if (kartAyar.seri?.baudRate) {
        const baudP = this.parametreBul('baudRate');
        if (baudP) baudP.degeri = kartAyar.seri.baudRate;
      }
      if (kartAyar.ws?.cihazKimlik) {
        const wsP = this.parametreBul('kartOkuyucuCihazKimlik');
        if (wsP && !wsP.degeri) wsP.degeri = kartAyar.ws.cihazKimlik;
      }
    } catch {}
  }
}
